import { safeDivision } from 'utils/format'
import {
  getReportExportValue,
  getSalesDataDisabledReason,
  isFieldDisabled
} from 'utils/reportDisabledFields'

export const roundSpreadsheetValue = value => {
  const number = Number(value) || 0
  return Math.round((number + Number.EPSILON) * 100) / 100
}

const getHasInputs = (entity, options) =>
  options.hasInputs ?? entity.hasInputs ?? true

const getObservedExportValue = (hasInputs, reason, getValue) =>
  hasInputs && !isFieldDisabled(reason) ? getValue() : '-'

export const formatStoreDataToExcel = (
  store,
  disabledFields = {},
  options = {}
) => {
  const salesDataReason = getSalesDataDisabledReason(disabledFields)
  const salesDataDisabled = isFieldDisabled(salesDataReason)
  const hasInputs = getHasInputs(store, options)

  return [
  'Loja',
  '-',
  salesDataDisabled ? '-' : store.total.sold ?? 0,
  getObservedExportValue(
    hasInputs,
    disabledFields.projection?.sold,
    () => store.projection?.sold ?? 0
  ),
  getReportExportValue(
    disabledFields.metric?.acDayGoal,
    store.metric.acDayGoal ?? 0
  ),
  salesDataDisabled ||
  isFieldDisabled(disabledFields.metric?.acDayGoal)
    ? '-'
    : safeDivision(store.total.sold, store.metric.acDayGoal),
  getReportExportValue(
    disabledFields.metric?.mainGoal,
    store.monthGoal ?? 0
  ),
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  '-',
  salesDataDisabled ? '-' : store.total.sales ?? 0,
  getReportExportValue(
    disabledFields.dynamicGoal?.sales,
    Math.ceil(store.dynamicGoal.sales)
  ),
  getObservedExportValue(
    hasInputs,
    salesDataReason,
    () => safeDivision(store.total.sold, store.total.sales)
  ),
  store.dynamicGoal.ticket,
  getObservedExportValue(
    hasInputs,
    salesDataReason,
    () => safeDivision(store.total.items, store.total.sales)
  ),
  store.dynamicGoal.itemsSale,
  getObservedExportValue(
    hasInputs,
    salesDataReason,
    () => safeDivision(store.total.sold, store.total.items)
  ),
  store.dynamicGoal.price,
  salesDataDisabled ? '-' : store.total.items ?? 0,
  getReportExportValue(
    disabledFields.dynamicGoal?.items,
    Math.ceil(store.dynamicGoal.items)
  )
  ]
}

export const formatOtherInputsStoreDataToExcel = (
  store,
  otherInputs,
  disabledFields = {},
  options = {}
) => {
  const salesDataDisabled = isFieldDisabled(
    getSalesDataDisabledReason(disabledFields)
  )

  return formatStoreDataToExcel({
  ...store,
  ...otherInputs.store,
  projection: { sold: otherInputs.store.soldProjection },
  metric: {
    ...store.metric,
    ...otherInputs.store.metric
  },
  total: salesDataDisabled
    ? { items: 0, sales: 0, sold: 0 }
    : otherInputs.store.total,
  dynamicGoal: otherInputs.store.dynamicGoal
}, disabledFields, options)
}

export const formatGroupDataToExcel = (
  group,
  disabledFields = {},
  options = {}
) => {
  const {
    name,
    total,
    projection,
    metric,
    monthGoal,
    dynamicGoal
  } = group
  const salesDataReason = getSalesDataDisabledReason(disabledFields)
  const salesDataDisabled = isFieldDisabled(salesDataReason)
  const hasInputs = getHasInputs(group, options)

  return [
    name,
    salesDataDisabled ? '-' : total.sold ?? 0,
    getObservedExportValue(
      hasInputs,
      disabledFields.projection?.sold,
      () => projection?.sold ?? 0
    ),
    getReportExportValue(
      disabledFields.metric?.acDayGoal,
      metric.acDayGoal ?? 0
    ),
    salesDataDisabled ||
    isFieldDisabled(disabledFields.metric?.acDayGoal)
      ? '-'
      : safeDivision(total.sold, metric.acDayGoal),
    getReportExportValue(
      disabledFields.metric?.mainGoal,
      monthGoal ?? 0
    ),
    salesDataDisabled ? '-' : total.sales ?? 0,
    getReportExportValue(
      disabledFields.dynamicGoal?.sales,
      Math.ceil(dynamicGoal.sales)
    ),
    getObservedExportValue(
      hasInputs,
      salesDataReason,
      () => safeDivision(total.sold, total.sales)
    ),
    dynamicGoal.ticket,
    getObservedExportValue(
      hasInputs,
      salesDataReason,
      () => safeDivision(total.items, total.sales)
    ),
    dynamicGoal.itemsSale,
    getObservedExportValue(
      hasInputs,
      salesDataReason,
      () => safeDivision(total.sold, total.items)
    ),
    dynamicGoal.price,
    salesDataDisabled ? '-' : total.items ?? 0,
    getReportExportValue(
      disabledFields.dynamicGoal?.items,
      Math.ceil(dynamicGoal.items)
    )
  ]
}

export const formatSellerDataToExcel = (
  seller,
  disabledFields = {},
  options = {}
) => {
  const commissionDisabled = isFieldDisabled(disabledFields.commission)
  const salesDataReason = getSalesDataDisabledReason(disabledFields)
  const salesDataDisabled = isFieldDisabled(salesDataReason)
  const hasInputs = getHasInputs(seller, options)

  return [
    seller.active
      ? seller.name.complete
      : `(INATIVO) ${seller.name.complete}`,
    seller.cpf ?? 'Não informado',
    salesDataDisabled ? '-' : seller.total.sold ?? 0,
    getObservedExportValue(
      hasInputs,
      disabledFields.projection?.sold,
      () => seller.projection?.sold ?? 0
    ),
    getReportExportValue(
      disabledFields.metric?.acDayGoal,
      seller.metric.acDayGoal ?? 0
    ),
    salesDataDisabled ||
    isFieldDisabled(disabledFields.metric?.acDayGoal)
      ? '-'
      : safeDivision(seller.total.sold, seller.metric.acDayGoal),
    getReportExportValue(
      disabledFields.metric?.goalValue,
      seller.monthGoal ?? 0
    ),
    getReportExportValue(
      disabledFields.projection?.goal,
      seller.projection?.goal?.name || '-'
    ),
    seller.daysWorking || '-',
    commissionDisabled ? '-' : seller.commission?.salary ?? '-',
    commissionDisabled ? '-' : seller.commission?.complement ?? '-',
    commissionDisabled
      ? '-'
      : seller.commission?.fixedCommission?.percentage ?? '-',
    commissionDisabled
      ? '-'
      : seller.commission?.fixedCommission?.value ?? '-',
    commissionDisabled
      ? '-'
      : seller.commission?.commission?.percentage ?? '-',
    commissionDisabled ? '-' : seller.commission?.commission?.value ?? '-',
    commissionDisabled ? '-' : seller.commission?.dsrDays ?? '-',
    commissionDisabled ? '-' : seller.commission?.dsr ?? '-',
    commissionDisabled ? '-' : seller.commission?.bonus?.percentage ?? '-',
    commissionDisabled ? '-' : seller.commission?.bonus?.value ?? '-',
    commissionDisabled
      ? '-'
      : seller.commission?.sumSectionsCommissions ??
        seller.commission?.sumCommission ??
        '-',
    getReportExportValue(
      disabledFields.projection?.commission,
      seller.projection?.commission?.sumSectionsCommissions ??
        seller.projection?.commission?.sumCommission ??
        '-'
    ),
    salesDataDisabled ? '-' : seller.total.sales ?? 0,
    getReportExportValue(
      disabledFields.dynamicGoal?.sales,
      Math.ceil(seller.dynamicGoal.sales)
    ),
    getObservedExportValue(
      hasInputs,
      salesDataReason,
      () => safeDivision(seller.total.sold, seller.total.sales)
    ),
    seller.dynamicGoal.ticket,
    getObservedExportValue(
      hasInputs,
      salesDataReason,
      () => safeDivision(seller.total.items, seller.total.sales)
    ),
    seller.dynamicGoal.itemsSale,
    getObservedExportValue(
      hasInputs,
      salesDataReason,
      () => safeDivision(seller.total.sold, seller.total.items)
    ),
    seller.dynamicGoal.price,
    salesDataDisabled ? '-' : seller.total.items ?? 0,
    getReportExportValue(
      disabledFields.dynamicGoal?.items,
      Math.ceil(seller.dynamicGoal.items)
    )
  ]
}
